import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { keyframes } from 'styled-components';
import { FiLoader, FiArrowRight } from 'react-icons/fi';
import { useBusiness } from '../../Common/Hooks/useBusiness';
import { isErr } from '../../Common/Utils/Result';
import { eventBus } from '../../Common/EventBus';
import { Notification } from '../../Components/Notifications';

const Page = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100%;
  padding: 3rem 1.5rem;
  gap: 1.25rem;
`;

const Card = styled.div`
  width: 100%;
  max-width: 720px;
  background: #F0FFFE;
  border: 1px solid #67D9D4;
  border-radius: 16px;
  padding: 2rem 2.25rem;
  display: flex;
  align-items: center;
  gap: 2rem;

  @media (max-width: 600px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 1.5rem;
    padding: 1.5rem;
  }
`;

const CardLeft = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
`;

const Badge = styled.span`
  display: inline-block;
  background: #CCFBF7;
  color: #0D9488;
  border: 1px solid #99F6E4;
  border-radius: 6px;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  padding: 3px 10px;
  width: fit-content;
`;

const CardTitle = styled.h2`
  margin: 0;
  color: #111827;
  font-size: 1.2rem;
  font-weight: 700;
  line-height: 1.3;
`;

const CardDesc = styled.p`
  margin: 0;
  color: #4B5563;
  font-size: 0.875rem;
  line-height: 1.55;
`;

const DiscountBadge = styled.span`
  display: inline-flex;
  align-items: center;
  background: #ECFDF5;
  color: #065F46;
  border: 1px solid #A7F3D0;
  border-radius: 6px;
  font-size: 0.78rem;
  font-weight: 600;
  padding: 4px 10px;
  width: fit-content;
`;

const CardRight = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.875rem;
  flex-shrink: 0;

  @media (max-width: 600px) {
    align-items: flex-start;
    width: 100%;
  }
`;

const PriceRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 0.25rem;
`;

const PriceCurrency = styled.span`
  color: #6B7280;
  font-size: 1.1rem;
  font-weight: 500;
`;

const PriceAmount = styled.span`
  color: #111827;
  font-size: 2.5rem;
  font-weight: 800;
  letter-spacing: -0.03em;
  line-height: 1;
`;

const PricePeriod = styled.span`
  color: #6B7280;
  font-size: 0.9rem;
`;

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const ActivateBtn = styled.button`
  background: #001d30;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 0.625rem 1.5rem;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: background 0.2s;
  white-space: nowrap;

  &:hover:not(:disabled) {
    background: #002c4a;
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }

  svg.spin {
    animation: ${spin} 1s linear infinite;
  }

  @media (max-width: 600px) {
    width: 100%;
    justify-content: center;
  }
`;

const EnterpriseCTA = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: #64748b;
  font-size: 0.82rem;
  padding: 0.25rem 0;
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  transition: color 0.2s;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 0;
    height: 1px;
    background: #67D9D4;
    transition: width 0.25s;
  }

  &:hover {
    color: #67D9D4;

    &::after {
      width: 100%;
    }

    svg {
      transform: translateX(3px);
    }
  }

  svg {
    transition: transform 0.2s;
    flex-shrink: 0;
  }
`;

export const StripePricing: React.FC = () => {
  const { t } = useTranslation();
  const { getPaymentUrl } = useBusiness();
  const [isLoading, setIsLoading] = useState(false);

  const handleActivate = async () => {
    if (isLoading) return;
    setIsLoading(true);
    try {
      const result = await getPaymentUrl(1);
      if (isErr(result)) {
        eventBus.emit('NOTIFICATION', Notification.buildNotification('error', t('pricing.stripePricing.notifications.paymentUrlFailed')));
        setIsLoading(false);
        return;
      }
      window.location.href = result.value;
    } catch {
      eventBus.emit('NOTIFICATION', Notification.buildNotification('error', t('pricing.stripePricing.notifications.unexpectedError')));
      setIsLoading(false);
    }
  };

  return (
    <Page>
      <Card>
        <CardLeft>
          <Badge>{t('pricing.stripePricing.badge')}</Badge>
          <CardTitle>{t('pricing.stripePricing.cardTitle')}</CardTitle>
          <CardDesc>
            {t('pricing.stripePricing.cardDescription')}
          </CardDesc>
          <DiscountBadge>
            {t('pricing.stripePricing.discountBadge')}
          </DiscountBadge>
        </CardLeft>

        <CardRight>
          <PriceRow>
            <PriceCurrency>€</PriceCurrency>
            <PriceAmount>49</PriceAmount>
            <PricePeriod>{t('pricing.stripePricing.pricePeriod')}</PricePeriod>
          </PriceRow>
          <ActivateBtn onClick={handleActivate} disabled={isLoading}>
            {isLoading ? (
              <><FiLoader className="spin" size={15} /> {t('pricing.stripePricing.activateButtonLoading')}</>
            ) : (
              t('pricing.stripePricing.activateButton')
            )}
          </ActivateBtn>
        </CardRight>
      </Card>

      <EnterpriseCTA onClick={() => window.open('mailto:hola@virtualmike.ai', '_blank')}>
        {t('pricing.stripePricing.enterpriseCta')}
        <FiArrowRight size={13} />
      </EnterpriseCTA>
    </Page>
  );
};
